import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  OnDestroy
} from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators, AbstractControl } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";

import { Subscription } from "rxjs";

import { TopicFormService } from "../../services/topic-form.service";

import { BHRHoliday } from "@onsip/common/interfaces/bhr";
import {
  uniqueDateValidatorForDays,
  uniqueDateValidatorForMonths,
  uniqueNameValidator
} from "./holiday.validator";

interface HolidayFormArray extends FormArray {
  controls: Array<FormGroup>;
} // typing hack

@Component({
  selector: "onsip-holiday-picker",
  templateUrl: "./holiday-picker.component.html",
  styleUrls: ["./holiday-picker.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HolidayPickerComponent implements OnInit, OnDestroy {
  @ViewChild("holidayNameInput") holidayNameInput!: ElementRef;
  /** holiday from config */
  @Input() initialValue: Array<BHRHoliday> | undefined;
  /** HACK to generate fixed length ngFor's in template */
  Arr = Array;
  /** Holiday FormArray, an array of FormGroup one for each holiday */
  holidayArray = new FormArray([], { updateOn: "change" }) as HolidayFormArray;

  private unsubscriber = new Subscription();

  constructor(
    private topicForm: TopicFormService,
    private translate: TranslateService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.holidayArray.clear();
    this.initForm();
    this.topicForm.addControl("holidays", this.holidayArray);

    // HACK to get proper mat errors for all siblings
    this.unsubscriber.add(
      this.holidayArray.valueChanges.subscribe(() => {
        this.holidayArray.controls.forEach((control: AbstractControl) => {
          if (control instanceof FormGroup) {
            Object.keys(control.controls).forEach((ctrl: string) => {
              control.controls[ctrl].updateValueAndValidity({ emitEvent: false });
              control.controls[ctrl].setErrors(control.controls[ctrl].errors);
            });
          }
        });
      })
    );

    this.unsubscriber.add(
      this.topicForm.getSubmitObs().subscribe(() => {
        this.holidayArray.markAllAsTouched();
        this.cdRef.markForCheck();
      })
    );
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  /** Adds a new holiday FormGroup to holidays FormArray */
  addHoliday() {
    const holiday = new FormGroup(
      {
        // Empty holiday form
        label: new FormControl("", {
          updateOn: "change",
          validators: [Validators.required, uniqueNameValidator()]
        }),
        m: new FormControl("", {
          updateOn: "change",
          validators: [Validators.required, uniqueDateValidatorForMonths()]
        }),
        d: new FormControl("", {
          updateOn: "change",
          validators: [Validators.required, uniqueDateValidatorForDays()]
        })
      },
      { updateOn: "change" }
    );
    this.holidayArray.push(holiday); // add new holiday to holidays FormArray
  }

  /** Deletes holiday at given index from FormArray */
  deleteHoliday(i: number) {
    this.holidayArray.removeAt(i);
  }

  getNameInputErrors(abstractControl: AbstractControl): string {
    const control = abstractControl as FormGroup;
    return control.controls.label.hasError("uniqueNameError")
      ? this.translate.instant("ONSIP_I18N.HOLIDAYS_CANNOT_HAVE_THE_SAME_NAME")
      : control.controls.label.hasError("required") ||
        control.controls.m.hasError("required") ||
        control.controls.d.hasError("required")
      ? this.translate.instant("ONSIP_I18N.HOLIDAY_NAME_MONTH_AND_DATE_MUST_BE_SET")
      : "";
  }

  getDateInputErrors(abstractControl: AbstractControl): string {
    const control = abstractControl as FormGroup;
    return control.controls.d.hasError("uniqueDateError") ||
      control.controls.m.hasError("uniqueDateError")
      ? this.translate.instant("ONSIP_I18N.DUPLICATE_DATE")
      : "";
  }

  /** Builds holiday form from initial value */
  private initForm() {
    if (this.initialValue && this.initialValue.length > 0) {
      this.initialValue.forEach(holiday => {
        this.holidayArray.push(
          new FormGroup(
            {
              label: new FormControl(holiday.label, {
                updateOn: "change",
                validators: [Validators.required, uniqueNameValidator()]
              }),
              m: new FormControl(holiday.m, {
                updateOn: "change",
                validators: [Validators.required, uniqueDateValidatorForMonths()]
              }),
              d: new FormControl(holiday.d, {
                updateOn: "change",
                validators: [Validators.required, uniqueDateValidatorForDays()]
              })
            },
            { updateOn: "change" }
          )
        );
      });
    } else {
      this.addHoliday();
    }
  }
}
